import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Mic,
  Music,
  FolderOpen,
  ArrowRight,
  User,
  Clock,
  Copy,
  Globe,
} from "lucide-react";

import narratorService from "@/services/narratorService";
import authService from "@/services/authService";
import NarratorHeader from "@/components/narrator/NarratorHeader";
import KPICard from "@/components/narrator/KPICard";
import ProjectCard from "@/components/narrator/ProjectCard";

function SetupProgressRing({ value }) {
  return (
    <div
      className="grid h-20 w-20 shrink-0 place-items-center rounded-full"
      style={{
        background: `conic-gradient(#c98905 ${value * 3.6}deg, #f2e5cd 0deg)`,
      }}
    >
      <div className="grid h-14 w-14 place-items-center rounded-full bg-white text-lg font-bold text-slate-950 dark:bg-[#090805] dark:text-[#f8efe0]">
        {value}%
      </div>
    </div>
  );
}

function NarratorPageShell({ children }) {
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#fffaf2] dark:bg-[#050504]">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_82%_8%,rgba(245,207,117,0.20),transparent_26%),linear-gradient(180deg,#ffffff_0%,#fffaf2_48%,#fff4e3_100%)] dark:bg-[radial-gradient(circle_at_78%_10%,rgba(224,165,27,0.30),transparent_24%),linear-gradient(180deg,#050504_0%,#090805_48%,#030302_100%)]" />
      <NarratorHeader />
      {children}
    </div>
  );
}

export default function NarratorDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [projects, setProjects] = useState([]);
  const [demos, setDemos] = useState([]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Try to get profile
      const profileData = await narratorService.getProfile().catch(() => null);
      setProfile(profileData);

      if (profileData) {
        // Load related data
        const [statusData, projectsData, demosData] = await Promise.all([
          narratorService.getOnboardingStatus().catch(() => null),
          narratorService.getProjects().catch(() => []),
          narratorService.getDemos().catch(() => []),
        ]);

        setOnboardingStatus(statusData);
        setProjects(Array.isArray(projectsData) ? projectsData : []);
        setDemos(Array.isArray(demosData) ? demosData : []);
      } else {
        setOnboardingStatus({
          has_profile: false,
          portfolio_status: "DRAFT",
          has_demo: false,
          suggested_next_step: "Create your narrator profile",
        });
      }
    } catch (error) {
      console.error("Error loading narrator data:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        authService.clearTokens();
        navigate("/login");
      } else if (error.response?.status === 403) {
        toast.error("Narrator role required. Please register as a narrator.");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async () => {
    try {
      await narratorService.createProfile();
      toast.success("Profile created! Let's complete your setup.");
      navigate("/narrator/onboarding");
    } catch (error) {
      console.error("Error creating profile:", error);
      toast.error("Failed to create profile");
    }
  };

  const activeProjects = projects.filter(
    (p) => p.status !== "COMPLETED" && p.status !== "QC_PASSED"
  );
  const approvedDemos = demos.filter((d) => d.status === "APPROVED");
  const pendingDemos = demos.filter(
    (d) => d.status === "DRAFT" || d.status === "SUBMITTED"
  );
  const setupProgress =
    onboardingStatus?.portfolio_status === "APPROVED"
      ? 100
      : onboardingStatus?.has_profile && onboardingStatus?.has_demo
        ? 80
        : onboardingStatus?.has_profile
          ? 50
          : 20;

  if (loading) {
  return (
    <NarratorPageShell>
      <div className="flex items-center justify-center h-[60vh] relative z-10">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#b77900] mx-auto"></div>
          <p className="mt-4 text-slate-700 dark:text-[#d5c8ae]">Loading narrator dashboard...</p>
        </div>
      </div>
    </NarratorPageShell>
  );
}


  // No profile - show onboarding prompt
  if (!profile) {
  return (
    <NarratorPageShell>
      <main className="container mx-auto px-6 py-12 relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#f5cf75] to-[#9b6700] flex items-center justify-center mx-auto mb-6 shadow-[0_16px_40px_rgba(185,119,0,0.24)]">
            <Mic className="w-10 h-10 text-white" />
          </div>

          <h1
            className="text-4xl font-bold text-black dark:text-[#f8efe0] mb-4"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Welcome, Narrator!
          </h1>

          <p className="text-lg text-slate-700 dark:text-[#d5c8ae] mb-8">
            Create your narrator profile to start receiving book assignments
            and showcase your voice talent.
          </p>

          <Button
            onClick={handleCreateProfile}
            className="bg-[#d99b13] text-lg px-8 py-6 text-black hover:bg-[#f0bd43]"
          >
            Create Your Profile
          </Button>
        </div>
      </main>
    </NarratorPageShell>
  );
}


  return (
    <NarratorPageShell>
      <main className="relative z-10 mx-auto max-w-[1640px] px-5 py-8 md:px-8">

        {/* Header */}
        <div className="relative mb-8 min-h-[150px] overflow-hidden px-6 py-10 text-center">
          <img
            src="/images/dashboard-gold-hero.png"
            alt=""
            className="pointer-events-none absolute inset-y-0 right-0 hidden h-full w-[42%] object-contain object-right opacity-95 lg:block"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#fffaf2] via-[#fffaf2]/95 to-[#fffaf2]/20 dark:from-[#050504] dark:via-[#050504]/92 dark:to-[#050504]/15" />
          <div className="relative">
            <h1
              className="text-4xl font-bold text-slate-950 dark:text-[#f8efe0] md:text-5xl"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Narrator <span className="text-[#b77900]">Dashboard</span>
            </h1>
            <p className="mt-4 text-slate-700 dark:text-[#d5c8ae]">
              Welcome back, {profile.display_name}!{" "}
              {onboardingStatus?.suggested_next_step}
            </p>
          </div>
        </div>

        {/* Onboarding Banner */}
        {onboardingStatus && onboardingStatus.portfolio_status !== "APPROVED" && (
          <Card className="mb-6 border border-[#e4bd73] bg-white/95 shadow-[0_16px_45px_rgba(94,64,18,0.08)] dark:border-[#8a6a24] dark:bg-[#0d0c09]/95 dark:shadow-[0_16px_45px_rgba(0,0,0,0.42)]">
            <CardContent className="p-6">
              <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-6">
                  <SetupProgressRing value={setupProgress} />
                  <div>
                  <h3 className="mb-1 text-xl font-semibold text-black dark:text-[#f8efe0]">
                    Complete Your Setup
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-[#d5c8ae]">
                    Almost there! Finish your profile to get matched with projects.
                  </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate("/narrator/onboarding")}
                  className="bg-[#d99b13] text-black hover:bg-[#f0bd43]"
                  data-testid="continue-onboarding-btn"
                >
                  Continue Setup
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            icon={FolderOpen}
            label="Active Projects"
            value={activeProjects.length}
            subtext={`${projects.filter((p) => p.status === "COMPLETED").length} completed`}
            onClick={() => navigate("/narrator/projects")}
            testId="kpi-projects"
          />
          <KPICard
            icon={Music}
            label="Demo Portfolio"
            value={approvedDemos.length}
            subtext={`${pendingDemos.length} pending review`}
            onClick={() => navigate("/narrator/demos")}
            testId="kpi-demos"
          />
          <KPICard
            icon={User}
            label="Profile Status"
            value={profile.is_verified ? "Verified" : "Pending"}
            subtext={profile.availability_status}
            onClick={() => navigate("/narrator/onboarding")}
            testId="kpi-profile"
          />
          <KPICard
            icon={Clock}
            label="Next Due Date"
            value={
              activeProjects[0]?.due_date
                ? new Date(activeProjects[0].due_date).toLocaleDateString()
                : "None"
            }
            subtext={activeProjects[0]?.book_title || "No active projects"}
            onClick={() =>
              activeProjects[0] &&
              navigate(`/narrator/projects/${activeProjects[0].id}`)
            }
            testId="kpi-deadline"
          />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Continue Project */}
          <div className="lg:col-span-2">
            <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_20px_60px_rgba(82,55,12,0.08)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95 dark:shadow-[0_20px_60px_rgba(0,0,0,0.45)]">
              <CardHeader className="pb-2">
                <CardTitle
                    className="text-xl flex items-center justify-between dark:text-[#f8efe0]"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  <span>Active Projects</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#9a6500] hover:bg-[#fff7e9] hover:text-[#7a5200] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]"
                    onClick={() => navigate("/narrator/projects")}
                    data-testid="view-all-projects-btn"
                  >
                    View All
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {activeProjects.length > 0 ? (
                  activeProjects.slice(0, 3).map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onClick={() => navigate(`/narrator/projects/${project.id}`)}
                    />
                  ))
                ) : (
                  <div className="py-12 text-center text-slate-600 dark:text-[#d5c8ae]">
                    <div className="mx-auto mb-5 grid h-24 w-24 place-items-center rounded-full bg-[#fff2d8] text-[#b77900]">
                      <FolderOpen className="h-12 w-12" />
                    </div>
                    <p className="font-semibold text-slate-950 dark:text-[#f8efe0]">No active projects yet</p>
                    <p className="mt-2 text-sm">
                      Complete your profile to receive assignments
                    </p>
                    <Button
                      onClick={() => navigate("/narrator/onboarding")}
                      className="mt-6 bg-[#d99b13] text-black hover:bg-[#f0bd43]"
                    >
                      Go to Profile
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_18px_55px_rgba(82,55,12,0.08)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95 dark:shadow-[0_18px_55px_rgba(0,0,0,0.42)]">
              <CardHeader className="pb-2">
                <CardTitle
                  className="text-lg dark:text-[#f8efe0]"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Quick Links
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-between border-[#eadfcd] bg-white text-slate-950 hover:bg-[#fff8eb] dark:border-[#4d3d16] dark:bg-[#11100d] dark:text-[#f8efe0] dark:hover:bg-[#1b1710]"
                  onClick={() => navigate("/narrator/demos")}
                  data-testid="quick-demos-btn"
                >
                  <span className="flex items-center">
                    <Music className="mr-3 h-4 w-4 text-[#c98905]" />
                    Manage Demos
                  </span>
                  <ArrowRight className="h-4 w-4 text-[#c98905]" />
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-between border-[#eadfcd] bg-white text-slate-950 hover:bg-[#fff8eb] dark:border-[#4d3d16] dark:bg-[#11100d] dark:text-[#f8efe0] dark:hover:bg-[#1b1710]"
                  onClick={() => navigate("/narrator/onboarding")}
                  data-testid="quick-profile-btn"
                >
                  <span className="flex items-center">
                    <User className="mr-3 h-4 w-4 text-[#c98905]" />
                    Edit Profile
                  </span>
                  <ArrowRight className="h-4 w-4 text-[#c98905]" />
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-between border-[#eadfcd] bg-white text-slate-950 hover:bg-[#fff8eb] dark:border-[#4d3d16] dark:bg-[#11100d] dark:text-[#f8efe0] dark:hover:bg-[#1b1710]"
                  onClick={() =>
                    window.open(`/n/${profile.slug}`, "_blank")
                  }
                  data-testid="quick-public-profile-btn"
                >
                  <span className="flex items-center">
                    <Globe className="mr-3 h-4 w-4 text-[#c98905]" />
                    View Public Profile
                  </span>
                  <ArrowRight className="h-4 w-4 text-[#c98905]" />
                </Button>
              </CardContent>
            </Card>

            {/* Public Profile Card */}
            <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_18px_55px_rgba(82,55,12,0.08)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95 dark:shadow-[0_18px_55px_rgba(0,0,0,0.42)]">
              <CardContent className="p-5">
                <p className="mb-3 text-sm font-semibold text-slate-950 dark:text-[#f8efe0]">Your public link:</p>
                <div className="flex gap-3">
                  <code className="flex-1 truncate rounded-lg border border-[#eadfcd] bg-white px-4 py-3 text-sm text-slate-600 dark:border-[#4d3d16] dark:bg-[#11100d] dark:text-[#d5c8ae]">
                    /n/{profile.slug}
                  </code>
                  <Button
                    variant="outline"
                    className="border-[#e0c89d] bg-white text-[#a46d00] hover:bg-[#fff8eb] dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]"
                    onClick={() => {
                      navigator.clipboard?.writeText(`${window.location.origin}/n/${profile.slug}`);
                      toast.success("Public link copied");
                    }}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Copy
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </NarratorPageShell>
  );
}
